/**
 * Catálogo de tipos de modelo (`/api/tipos-modelo`).
 *
 * El campo `programas` es un string descriptivo legacy sin relación con la entidad
 * `Area`: no es una lista de programas evaluables y no debe parsearse como tal.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import TipoModelo from '@/interfaces/TipoModelo'
import { http } from '@/services/http'

/**
 * Obtiene tipo modelo · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns TipoModelo[]
 */
export async function obtenerTipoModelos(url: string): Promise<TipoModelo[]> {
  const result = await http.get<TipoModelo[]>(url)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Obtiene tipo modelo · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns TipoModelo | null
 */
export async function obtenerTipoModeloPorId(url: string, id: number): Promise<TipoModelo | null> {
  const result = await http.get<TipoModelo>(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea tipo modelo · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns TipoModelo | null
 */
export async function crearTipoModeloService(url: string, tipo: Omit<TipoModelo, 'id_tipo_modelo'>): Promise<TipoModelo | null> {
  const result = await http.post<TipoModelo>(url, tipo)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Actualiza tipo modelo · petición `PUT`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns TipoModelo | null
 */
export async function actualizarTipoModeloService(url: string, id: number, tipo: Partial<TipoModelo>): Promise<TipoModelo | null> {
  const result = await http.put<TipoModelo>(`${url.replace(/\/$/, '')}/${id}`, tipo)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina tipo modelo · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarTipoModeloService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok
}
